"use client";
import React from "react";
import { isDateInPast } from "@/lib/date";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import { getCourseRunStartText } from "@/lib/course/utils/getCourseRunTilePrimaryText";
import { getCourseRunEndText } from "@/lib/course/utils/getCourseRunTitleSecondaryText";
import { CourseEnrollProps } from "@/components/CourseContent/EnrollButton/types";
import useCourseEnrollment from "../hooks/useCourseEnrollment";
import CourseEnrollButton from "../CourseEnrollButtons";
import AuditLink from "../CourseEnrollButtons/AuditLink";
import MultipleStartDateModal from "@/components/CourseContent/ContentRefresh/MultipleStartDateModal";
import Icon from "@/components/icons";
import SinglePricingCard from "@/components/GeoPricing/SinglePricingCard";
import useTranslations from "@/i18n/useTranslations";

function CourseEnrollInlineHeader({
  buttonClass = "",
  className = "",
  courseRun,
  course,
  showEndDate = true,
  showMobileAuditLink = true,
  name = "",
  location = "",
  showStartDate = true,
  showOnlyCta = false,
  seeMoreDatesHandler,
  straightToCheckout,
  pricingData,
}: CourseEnrollProps) {
  const t = useTranslations("Course");
  const translate =
    t as unknown as import("@/lib/course/utils/getCourseInformation").CourseInfoTranslate;
  const {
    isUserEnrolledInRun,
    isCourseEnrollable,
    isPaidLeadLoading,
    isCourseArchived,
  } = useCourseEnrollment({
    courseRun,
    course,
  });

  if (isPaidLeadLoading) {
    return (
      <div className="flex flex-row gap-3 items-center bg-putty-100 p-4 rounded-xl border-putty-400 border">
        <Skeleton role="status" className="h-11 w-56 rounded-none" />
      </div>
    );
  }
  //
  const enrollmentEnded = courseRun.enrollmentEnd
    ? isDateInPast(courseRun.enrollmentEnd)
    : false;

  const courseRunPrice = getCoursePrice(courseRun);

  return (
    <div
      className={cn(
        "w-auto xl:w-[calc(50%-12px)] [&:only-child]:w-auto",
        className || "",
      )}
    >
      <div
        className={cn(
          "flex flex-col md:flex-row gap-4 sm:gap-2 items-center justify-between bg-putty-100 p-4 rounded-xl border-putty-400 border",
          showOnlyCta && "justify-center",
        )}
      >
        {!showOnlyCta && (
          <>
            {isCourseArchived || !isCourseEnrollable ? (
              <span className="font-bold text-base">
                {t("thisCourseIsArchived")}
              </span>
            ) : (
              <div className="text-center md:text-left">
                {seeMoreDatesHandler ? (
                  <div className="text-sm mb-1">{t("nextCourseStarts")}</div>
                ) : null}
                <div
                  className={`${showStartDate ? "block" : "hidden md:block"} font-bold md:mb-1`}
                >
                  {getCourseRunStartText(
                    courseRun,
                    !!seeMoreDatesHandler,
                    translate,
                  )}
                </div>
                {showEndDate ? (
                  <div
                    className={`md:mt-1 ${seeMoreDatesHandler ? "text-sm" : ""}`}
                  >
                    {getCourseRunEndText(courseRun, translate)}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}

        {isCourseEnrollable ? (
          <div
            className={`flex flex-col gap-2 sm:gap-3 items-center md:flex-row ${showOnlyCta ? "justify-center w-full" : ""}`}
          >
            <CourseEnrollButton
              buttonClass={buttonClass}
              courseRun={courseRun}
              course={course}
              name={name}
              location={location}
              price={courseRunPrice}
              straightToCheckout={straightToCheckout}
            />
            {!showOnlyCta && (
              <div
                className={`${showMobileAuditLink ? "block" : "hidden md:block"} `}
              >
                <AuditLink
                  courseRun={courseRun}
                  course={course}
                  name={name}
                  location={location}
                  variant="button"
                />
              </div>
            )}
          </div>
        ) : !isUserEnrolledInRun && enrollmentEnded ? (
          <span>{t("futureDatesToBeAnnounced")}</span>
        ) : null}
      </div>

      {/* See more dates section - appears below the inline box */}
      {seeMoreDatesHandler ? (
        <div className="mt-2 flex justify-start">
          <MultipleStartDateModal course={course} showEnrollmentCount={false}>
            <button type="button" className="flex flex-row gap-1 items-center">
              <Icon type="calendar-checked" />
              <span className="underline underline-offset-2 text-sm">
                {t("seeMoreDates")}
              </span>
            </button>
          </MultipleStartDateModal>
        </div>
      ) : null}
    </div>
  );
}

export default CourseEnrollInlineHeader;
